import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { axiomTheme, toneAlpha, toneColor, type Tone as WorkspaceTone } from '../../../styles/designTokens';

export type Tone = WorkspaceTone;

export const CreditWorkspaceShell = styled.div`
  padding: ${axiomTheme.spacing.page};
  color: ${axiomTheme.color.text.secondary};
`;

export const CreditWorkspaceHero = styled.div`
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.hero};
  background: ${axiomTheme.surface.hero};
  box-shadow: ${axiomTheme.shadow.panel};
  padding: 1.25rem;
  margin-bottom: 1rem;
`;

export const HeroTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1.25rem;
  align-items: flex-start;
  flex-wrap: wrap;
`;

export const Eyebrow = styled.div<{ $tone?: Tone }>`
  color: ${p => toneColor(p.$tone || 'cyan')};
  font-size: ${axiomTheme.typography.eyebrow.size};
  font-weight: ${axiomTheme.typography.eyebrow.weight};
  text-transform: uppercase;
  letter-spacing: ${axiomTheme.typography.eyebrow.tracking};
  margin-bottom: 0.45rem;
`;

export const HeroTitle = styled.h2`
  margin: 0;
  color: ${axiomTheme.color.text.primary};
  font-size: 1.35rem;
  line-height: 1.2;
  font-weight: 750;
  letter-spacing: -0.02em;
`;

export const HeroSubtitle = styled.div`
  margin-top: 0.45rem;
  max-width: 820px;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.9rem;
  line-height: 1.55;
`;

export const ViewSwitch = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.28rem;
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.pill};
  background: ${axiomTheme.surface.input};
`;

export const ViewPill = styled.button<{ $active?: boolean }>`
  border: 1px solid ${p => p.$active ? axiomTheme.color.border.focus : 'transparent'};
  background: ${p => p.$active ? 'rgba(14, 116, 144, 0.24)' : 'transparent'};
  color: ${p => p.$active ? '#e0f2fe' : axiomTheme.color.text.muted};
  border-radius: ${axiomTheme.radius.pill};
  padding: 0.48rem 0.75rem;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: all 0.16s ease;

  &:hover {
    color: ${axiomTheme.color.text.primary};
    background: rgba(148, 163, 184, 0.1);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${axiomTheme.shadow.focus};
  }
`;

export const CreditWorkspaceBody = styled.div<{ $railCollapsed?: boolean }>`
  display: grid;
  grid-template-columns: ${p => p.$railCollapsed ? '64px minmax(0, 1fr)' : 'minmax(230px, 270px) minmax(0, 1fr)'};
  gap: 1rem;
  align-items: start;
  transition: grid-template-columns 0.18s ease;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const WorkspaceRail = styled.aside<{ $collapsed?: boolean }>`
  position: sticky;
  top: 88px;
  min-width: ${p => p.$collapsed ? '64px' : '0'};
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.xxl};
  background: ${axiomTheme.surface.rail};
  overflow: hidden;

  @media (max-width: 980px) {
    position: static;
  }
`;

const RailToggleButton = styled.button<{ $collapsed?: boolean }>`
  width: calc(100% - 1.1rem);
  min-height: 40px;
  margin: 0.55rem;
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.lg};
  background: ${p => p.$collapsed ? 'rgba(14, 116, 144, 0.20)' : 'rgba(15, 23, 42, 0.62)'};
  color: ${axiomTheme.color.text.secondary};
  display: inline-flex;
  align-items: center;
  justify-content: ${p => p.$collapsed ? 'center' : 'space-between'};
  gap: 0.55rem;
  padding: ${p => p.$collapsed ? '0' : '0 0.75rem'};
  cursor: pointer;
  transition: all 0.16s ease;

  svg {
    width: 17px;
    height: 17px;
    color: ${axiomTheme.color.tone.cyan};
    flex: 0 0 auto;
  }

  &:hover {
    border-color: ${axiomTheme.color.border.focus};
    color: ${axiomTheme.color.text.primary};
    background: rgba(14, 116, 144, 0.24);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${axiomTheme.shadow.focus};
  }
`;

const RailToggleLabel = styled.span`
  color: ${axiomTheme.color.text.muted};
  font-size: ${axiomTheme.typography.label.size};
  font-weight: ${axiomTheme.typography.label.weight};
  text-transform: uppercase;
  letter-spacing: ${axiomTheme.typography.label.tracking};
`;

export const WorkspaceRailToggle = ({
  collapsed,
  onToggle,
  label = 'Workflows',
}: {
  collapsed: boolean;
  onToggle: () => void;
  label?: string;
}) => (
  <RailToggleButton
    type="button"
    $collapsed={collapsed}
    aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
    title={collapsed ? `Expand ${label}` : `Collapse ${label}`}
    onClick={onToggle}
  >
    {!collapsed && <RailToggleLabel>{label}</RailToggleLabel>}
    {collapsed ? <ChevronRight /> : <ChevronLeft />}
  </RailToggleButton>
);

export const CollapsedRail = styled.div`
  padding: 0 0.55rem 0.7rem;
`;

export const CollapsedRailSection = styled.div`
  display: grid;
  gap: 0.45rem;
  padding-top: 0.55rem;
  margin-top: 0.55rem;
  border-top: 1px solid ${axiomTheme.color.border.subtle};

  &:first-child {
    margin-top: 0;
    border-top: none;
    padding-top: 0;
  }
`;

export const CollapsedRailItem = styled.button<{ $active?: boolean; $tone?: Tone }>`
  width: 42px;
  height: 42px;
  border: 1px solid ${p => p.$active ? toneAlpha(p.$tone || 'cyan', '66') : axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.lg};
  background: ${p => p.$active ? toneAlpha(p.$tone || 'cyan', '22') : 'rgba(15, 23, 42, 0.56)'};
  color: ${p => p.$active ? toneColor(p.$tone || 'cyan') : axiomTheme.color.text.faint};
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.16s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: ${axiomTheme.color.text.primary};
    background: rgba(148, 163, 184, 0.10);
    border-color: ${axiomTheme.color.border.focus};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${axiomTheme.shadow.focus};
  }
`;

export const RailSection = styled.div`
  padding: 0.9rem;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};

  &:last-child {
    border-bottom: none;
  }
`;

export const RailSectionLabel = styled.div`
  color: ${axiomTheme.color.text.faint};
  font-size: ${axiomTheme.typography.label.size};
  font-weight: ${axiomTheme.typography.label.weight};
  text-transform: uppercase;
  letter-spacing: ${axiomTheme.typography.eyebrow.tracking};
  margin-bottom: 0.65rem;
`;

export const RailSectionHeader = styled.button`
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0 0 0.65rem;
  color: inherit;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  text-align: left;

  ${RailSectionLabel} {
    margin-bottom: 0;
  }
`;

export const RailSectionMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: ${axiomTheme.color.text.faint};
  font-size: 0.65rem;
  font-weight: 800;
`;

export const RailChevron = styled.span<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  transform: rotate(${p => p.$open ? '180deg' : '0deg'});
  transition: transform 0.16s ease;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const RailItem = styled.button<{ $active?: boolean; $tone?: Tone }>`
  width: 100%;
  border: 1px solid ${p => p.$active ? toneAlpha(p.$tone || 'cyan', '55') : 'transparent'};
  background: ${p => p.$active ? toneAlpha(p.$tone || 'cyan', '1a') : 'transparent'};
  color: ${p => p.$active ? axiomTheme.color.text.primary : axiomTheme.color.text.secondary};
  border-radius: ${axiomTheme.radius.lg};
  padding: 0.75rem;
  text-align: left;
  cursor: pointer;
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: 0.7rem;
  transition: all 0.16s ease;

  & + & {
    margin-top: 0.3rem;
  }

  svg {
    width: 17px;
    height: 17px;
    color: ${p => p.$active ? toneColor(p.$tone || 'cyan') : axiomTheme.color.text.faint};
    margin-top: 1px;
  }

  &:hover {
    background: rgba(148, 163, 184, 0.08);
    color: ${axiomTheme.color.text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${axiomTheme.shadow.focus};
  }
`;

export const RailItemLabel = styled.div`
  font-size: 0.82rem;
  font-weight: 800;
`;

export const RailItemDescription = styled.div`
  color: ${axiomTheme.color.text.muted};
  margin-top: 0.18rem;
  font-size: 0.68rem;
  line-height: 1.35;
`;

export const WorkspaceMain = styled.main`
  min-width: 0;
`;

export const WorkspacePanel = styled.section`
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.xxl};
  background: ${axiomTheme.surface.panel};
  overflow: hidden;
`;

export const PanelHeader = styled.div`
  padding: 1rem 1.15rem;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};
  background: ${axiomTheme.surface.panelHeader};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const PanelTitle = styled.h3`
  margin: 0;
  color: ${axiomTheme.color.text.primary};
  font-size: 1rem;
  font-weight: 800;
`;

export const PanelSubtitle = styled.div`
  margin-top: 0.3rem;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.78rem;
  line-height: 1.45;
`;

export const PanelBody = styled.div`
  padding: 1rem;
`;

export const MetricStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

export const MetricCard = styled.div<{ $tone?: Tone }>`
  border: 1px solid ${axiomTheme.color.border.default};
  border-left: 3px solid ${p => toneColor(p.$tone || 'cyan')};
  border-radius: ${axiomTheme.radius.xl};
  background: ${axiomTheme.surface.card};
  padding: 0.85rem;
  min-width: 0;
`;

export const MetricLabel = styled.div`
  color: ${axiomTheme.color.text.muted};
  font-size: ${axiomTheme.typography.label.size};
  font-weight: ${axiomTheme.typography.label.weight};
  text-transform: uppercase;
  letter-spacing: ${axiomTheme.typography.label.tracking};
`;

export const MetricValue = styled.div`
  color: ${axiomTheme.color.text.primary};
  margin-top: 0.28rem;
  font-size: 1.14rem;
  font-weight: 850;
  letter-spacing: -0.02em;
  overflow-wrap: anywhere;
`;

export const MetricSubtext = styled.div<{ $tone?: Tone }>`
  color: ${p => p.$tone ? toneColor(p.$tone) : axiomTheme.color.text.faint};
  margin-top: 0.25rem;
  font-size: 0.68rem;
  font-weight: 650;
`;

export const StatusPill = styled.span<{ $tone?: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid ${p => toneAlpha(p.$tone || 'slate', '55')};
  background: ${p => toneAlpha(p.$tone || 'slate', '1c')};
  color: ${p => toneColor(p.$tone || 'slate')};
  border-radius: ${axiomTheme.radius.pill};
  padding: 0.25rem 0.55rem;
  font-size: 0.68rem;
  font-weight: 850;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const TableShell = styled.div`
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.xl};
  overflow: auto;
  background: ${axiomTheme.surface.cardMuted};
`;

export const DataTable = styled.table`
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;

  th, td {
    text-align: left;
    padding: 0.78rem 0.85rem;
    border-bottom: 1px solid ${axiomTheme.color.border.subtle};
    font-size: 0.78rem;
  }

  th {
    color: ${axiomTheme.color.text.muted};
    font-size: 0.66rem;
    font-weight: 850;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    background: rgba(15, 23, 42, 0.88);
    white-space: nowrap;
  }

  td {
    color: ${axiomTheme.color.text.secondary};
    vertical-align: top;
  }

  tbody tr {
    transition: background 0.14s ease;
  }

  tbody tr:hover {
    background: rgba(30, 41, 59, 0.56);
  }
`;

export const RowTitle = styled.div`
  color: ${axiomTheme.color.text.primary};
  font-weight: 800;
`;

export const RowSubtext = styled.div`
  color: ${axiomTheme.color.text.faint};
  margin-top: 0.2rem;
  font-size: 0.68rem;
`;

export const ActionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

export const WorkspaceButton = styled.button.attrs({ type: 'button' })<{ $variant?: 'primary' | 'danger' | 'quiet' }>`
  border: 1px solid ${p => {
    if (p.$variant === 'danger') return 'rgba(248, 113, 113, 0.45)';
    if (p.$variant === 'primary') return axiomTheme.color.border.focus;
    return axiomTheme.color.border.default;
  }};
  background: ${p => {
    if (p.$variant === 'danger') return 'rgba(127, 29, 29, 0.24)';
    if (p.$variant === 'primary') return 'linear-gradient(135deg, rgba(14, 116, 144, 0.72), rgba(37, 99, 235, 0.52))';
    return 'rgba(15, 23, 42, 0.58)';
  }};
  color: ${p => {
    if (p.$variant === 'danger') return '#fecaca';
    if (p.$variant === 'primary') return '#e0f2fe';
    return axiomTheme.color.text.secondary;
  }};
  border-radius: ${axiomTheme.radius.md};
  padding: 0.5rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.16s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(226, 232, 240, 0.36);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    transform: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${axiomTheme.shadow.focus};
  }
`;

export const EmptyStateBox = styled.div`
  border: 1px dashed ${axiomTheme.color.border.strong};
  border-radius: ${axiomTheme.radius.xl};
  color: ${axiomTheme.color.text.faint};
  padding: 1.4rem;
  text-align: center;
  background: rgba(15, 23, 42, 0.36);
`;

export const DrawerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.76);
  z-index: 1200;
`;

export const ControlRoomDrawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  width: min(1120px, 96vw);
  height: 100vh;
  overflow-y: auto;
  background:
    radial-gradient(circle at top right, rgba(56, 189, 248, 0.13), transparent 32%),
    ${axiomTheme.color.canvas};
  border-left: 1px solid ${axiomTheme.color.border.strong};
  padding: 1.25rem;
  z-index: 1201;
  color: ${axiomTheme.color.text.secondary};
`;

export const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

export const DrawerTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 1rem 0;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};
  padding-bottom: 0.7rem;
`;

export const DrawerTab = styled.button<{ $active?: boolean }>`
  border: 1px solid ${p => p.$active ? axiomTheme.color.border.focus : axiomTheme.color.border.default};
  background: ${p => p.$active ? 'rgba(14, 116, 144, 0.24)' : 'rgba(15, 23, 42, 0.44)'};
  color: ${p => p.$active ? '#e0f2fe' : axiomTheme.color.text.muted};
  border-radius: ${axiomTheme.radius.pill};
  padding: 0.48rem 0.75rem;
  font-size: 0.72rem;
  font-weight: 850;
  cursor: pointer;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const InfoCard = styled.div`
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.lg};
  padding: 0.8rem;
  background: ${axiomTheme.surface.card};
`;

export const InfoLabel = styled.div`
  color: ${axiomTheme.color.text.muted};
  font-size: 0.64rem;
  font-weight: 850;
  text-transform: uppercase;
  letter-spacing: 0.09em;
`;

export const InfoValue = styled.div`
  color: ${axiomTheme.color.text.primary};
  margin-top: 0.35rem;
  font-weight: 800;
  overflow-wrap: anywhere;
`;

/* Control-room definition groups — the drawer standard for reference data
   (label-left / value-right hairline rows in titled cards), shared by the
   ABF / LL / PC facility drawers so the family reads as one system. */
export const DefColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-top: 0.8rem;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

export const DefGroup = styled.div`
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.42);
  padding: 0.85rem 0.95rem 0.55rem;
  min-width: 0;
`;

export const DefGroupTitle = styled.div`
  color: #64748b;
  font-size: 0.64rem;
  font-weight: 850;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 0.35rem;
`;

export const DefRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  padding: 0.42rem 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.08);

  &:last-child { border-bottom: none; }
`;

export const DefLabel = styled.span`
  color: #94a3b8;
  font-size: 0.76rem;
`;

export const DefValue = styled.span`
  color: #f8fafc;
  font-size: 0.8rem;
  font-weight: 700;
  text-align: right;
  overflow-wrap: anywhere;
`;

export const EvidenceChip = styled.span<{ $tone?: 'cyan' | 'amber' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid ${p => (p.$tone === 'amber' ? 'rgba(251, 191, 36, 0.4)' : 'rgba(56, 189, 248, 0.35)')};
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.5);
  color: ${p => (p.$tone === 'amber' ? '#fcd34d' : '#7dd3fc')};
  font-size: 0.66rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.22rem 0.6rem;
`;

export const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

export const TimelineItem = styled.div<{ $tone?: Tone }>`
  border-left: 3px solid ${p => toneColor(p.$tone || 'cyan')};
  border-radius: 0 ${axiomTheme.radius.lg} ${axiomTheme.radius.lg} 0;
  background: rgba(15, 23, 42, 0.58);
  padding: 0.85rem 0.95rem;
`;

export const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.76);
  z-index: 1300;
  display: grid;
  place-items: center;
`;

export const ActionModal = styled.div`
  width: min(620px, 94vw);
  border: 1px solid ${axiomTheme.color.border.strong};
  border-radius: ${axiomTheme.radius.xxl};
  background: ${axiomTheme.color.canvas};
  box-shadow: ${axiomTheme.shadow.drawer};
  padding: 1.25rem;
`;

export const FieldLabel = styled.label`
  display: block;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.72rem;
  font-weight: 800;
  margin-top: 0.8rem;

  input {
    width: 100%;
    box-sizing: border-box;
    margin-top: 0.4rem;
    border: 1px solid ${axiomTheme.color.border.strong};
    background: ${axiomTheme.surface.input};
    color: ${axiomTheme.color.text.primary};
    border-radius: ${axiomTheme.radius.md};
    padding: 0.7rem;
  }
`;

export const ImpactPreview = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
  margin-top: 0.95rem;
  padding: 0.75rem;
  border: 1px solid rgba(56, 189, 248, 0.18);
  border-radius: ${axiomTheme.radius.xl};
  background: rgba(14, 116, 144, 0.12);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
  margin-top: 1rem;
`;

export const moneyCompact = (value: any, currency = 'USD') =>
  value == null || Number.isNaN(Number(value)) ? '--' : new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: Math.abs(Number(value)) >= 1000000 ? 1 : 0,
    notation: Math.abs(Number(value)) >= 1000000 ? 'compact' : 'standard',
  }).format(Number(value));

export const moneyFull = (value: any, currency = 'USD') =>
  value == null || Number.isNaN(Number(value)) ? '--' : new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value));

export const percent = (value: any, digits = 2) =>
  value == null || Number.isNaN(Number(value)) ? '--' : `${Number(value).toFixed(digits)}%`;

export const numberCompact = (value: any) =>
  value == null || Number.isNaN(Number(value)) ? '--' : new Intl.NumberFormat('en-US', {
    notation: Math.abs(Number(value)) >= 10000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(Number(value)) >= 10000 ? 1 : 0,
  }).format(Number(value));
